import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ScrollText, Handshake, Loader2, Plus, Trash2, Save, GripVertical } from "lucide-react";

function CondEditor({ items, setItems, testPrefix, placeholderT, placeholderX }) {
  const setRow = (i, k, v) => setItems((arr) => arr.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const move = (i, dir) => setItems((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const next = [...arr];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  return (
    <div className="space-y-3">
      {items.map((c, i) => (
        <Card key={i} className="rounded-lg border-border p-4" data-testid={`${testPrefix}-row-${i}`}>
          <div className="flex items-start gap-3">
            <div className="flex flex-col gap-1 pt-1 text-muted-foreground">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-30 hover:text-foreground" data-testid={`${testPrefix}-up-${i}`}>▲</button>
              <GripVertical className="h-4 w-4 opacity-40" />
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="disabled:opacity-30 hover:text-foreground" data-testid={`${testPrefix}-down-${i}`}>▼</button>
            </div>
            <div className="flex-1 space-y-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Titolo</Label>
                <Input value={c.titolo} onChange={(e) => setRow(i, "titolo", e.target.value)} placeholder={placeholderT} data-testid={`${testPrefix}-titolo-${i}`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Testo</Label>
                <Textarea value={c.testo} onChange={(e) => setRow(i, "testo", e.target.value)} placeholder={placeholderX} rows={3} data-testid={`${testPrefix}-testo-${i}`} />
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-destructive hover:text-destructive shrink-0" data-testid={`${testPrefix}-remove-${i}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      ))}
      <Button variant="outline" size="sm" onClick={() => setItems((arr) => [...arr, { titolo: "", testo: "" }])} data-testid={`${testPrefix}-add`}>
        <Plus className="h-4 w-4 mr-1.5" /> Aggiungi voce
      </Button>
    </div>
  );
}

export default function Condizioni() {
  const [base, setBase] = useState(null);
  const [fornitura, setFornitura] = useState([]);
  const [commerciali, setCommerciali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/settings/condizioni")
      .then((r) => { setBase(r.data); setFornitura(r.data.fornitura || []); setCommerciali(r.data.commerciali || []); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const clean = (arr) => arr.map((c) => ({ titolo: (c.titolo || "").trim(), testo: (c.testo || "").trim() }));
    const f = clean(fornitura), m = clean(commerciali);
    if ([...f, ...m].some((c) => !c.titolo || !c.testo)) {
      toast.error("Compila titolo e testo per ogni voce");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/condizioni", { ...base, fornitura: f, commerciali: m });
      setBase(data); setFornitura(data.fornitura); setCommerciali(data.commerciali);
      toast.success("Condizioni salvate");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Condizioni chiavi in mano</h1>
          <p className="text-xs text-muted-foreground">Condizioni di fornitura e commerciali richiamate in Outlook e nei documenti</p>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-condizioni-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva condizioni
        </Button>
      </header>

      <div className="p-4 lg:p-8">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <Tabs defaultValue="fornitura" className="max-w-4xl">
            <TabsList className="mb-5">
              <TabsTrigger value="fornitura" data-testid="tab-fornitura"><ScrollText className="h-4 w-4 mr-1.5" /> Condizioni di fornitura</TabsTrigger>
              <TabsTrigger value="commerciali" data-testid="tab-commerciali"><Handshake className="h-4 w-4 mr-1.5" /> Condizioni commerciali</TabsTrigger>
            </TabsList>
            <TabsContent value="fornitura">
              <p className="text-xs text-muted-foreground mb-4">Mostrate nell'Outlook cliente. Es. validità offerta, garanzia, consegna, tempistiche, sopralluogo.</p>
              <CondEditor items={fornitura} setItems={setFornitura} testPrefix="fornitura" placeholderT="Garanzia" placeholderX="Garanzia di 2 anni sull'installazione…" />
            </TabsContent>
            <TabsContent value="commerciali">
              <p className="text-xs text-muted-foreground mb-4">Condizioni Generali di Vendita richiamate nell'Outlook e stampate nel PDF della proposta contrattuale.</p>
              <CondEditor items={commerciali} setItems={setCommerciali} testPrefix="commerciali" placeholderT="Art. 1 - Premesse" placeholderX="Il Committente intende realizzare…" />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
